import { useEffect, useState } from 'react';
import { api2, ApiError, type CheckinDto, type QuestionDto } from '../api.ts';
import AutoTextarea from '../components/AutoTextarea.tsx';

/**
 * The two-way tab. Three things that all flow back to the team rather than
 * out to volunteers:
 *
 *  - the weekly check-in, which goes to the coordination team and never to
 *    one named person
 *  - an event review after anything you helped run, including who deserves a
 *    thank you — those names come straight back out as notes
 *  - a question, answered within a day
 */
export default function Team(): React.ReactElement {
  const [checkin, setCheckin] = useState<CheckinDto | null>(null);
  const [questions, setQuestions] = useState<QuestionDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([api2.checkin(), api2.questions()])
      .then(([c, q]) => {
        setCheckin(c.checkin);
        setQuestions(q.questions);
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Could not load this.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p className="muted">Loading…</p>;

  return (
    <div className="stack-lg">
      <header>
        <h1>You & the team</h1>
        <p className="soft" style={{ marginTop: 6 }}>
          What you did, what would help you do more, and anything you want to ask.
        </p>
      </header>

      {error && (
        <p className="notice alert" role="alert">
          {error}
        </p>
      )}
      {saved && <p className="notice mint">{saved}</p>}

      <Checkin current={checkin} onSaved={(c) => { setCheckin(c); setSaved('Thank you — that goes to the coordination team.'); }} />
      <EventReview onSaved={(m) => setSaved(m)} />
      <Questions questions={questions} onAsked={(q, m) => { setQuestions(q); setSaved(m); }} />
    </div>
  );
}

function Checkin({
  current,
  onSaved,
}: {
  current: CheckinDto | null;
  onSaved: (checkin: CheckinDto) => void;
}): React.ReactElement {
  const [did, setDid] = useState(current?.did ?? '');
  const [wentWell, setWentWell] = useState(current?.wentWell ?? '');
  const [wentBadly, setWentBadly] = useState(current?.wentBadly ?? '');
  const [wouldHelp, setWouldHelp] = useState(current?.wouldHelp ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The server refuses an all-blank check-in — don't offer a Send that
  // can only come back with an error. One real line is enough.
  const hasSomething = [did, wentWell, wentBadly, wouldHelp].some((line) => line.trim());

  const save = async () => {
    if (!hasSomething) return setError('Write a line in any one of these — whichever is easiest.');
    setBusy(true);
    setError(null);
    try {
      const res = await api2.submitCheckin({ did, wentWell, wentBadly, wouldHelp });
      onSaved(res.checkin);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save that.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card stack">
      <div>
        <h2>This week</h2>
        <p className="small muted">
          Goes to the Isha coordination team, never to one named person.
          {current ? ' You have already sent one this week — saving again replaces it.' : ''}
        </p>
      </div>

      <div className="field">
        <label htmlFor="did">What did you do?</label>
        <AutoTextarea id="did" value={did} onChange={(e) => setDid(e.target.value)} />
      </div>
      <div className="field">
        <label htmlFor="well">What went well?</label>
        <AutoTextarea id="well" value={wentWell} onChange={(e) => setWentWell(e.target.value)} />
      </div>
      <div className="field">
        <label htmlFor="badly">What did not?</label>
        <AutoTextarea id="badly" value={wentBadly} onChange={(e) => setWentBadly(e.target.value)} />
      </div>
      <div className="field">
        <label htmlFor="help">What would help you do more?</label>
        <AutoTextarea id="help" value={wouldHelp} onChange={(e) => setWouldHelp(e.target.value)} />
      </div>

      {error && (
        <p className="notice alert" role="alert">
          {error}
        </p>
      )}
      <div>
        <button className="btn" onClick={() => void save()} disabled={busy}>
          {busy ? 'Sending…' : current ? 'Update it' : 'Send'}
        </button>
        {!hasSomething && (
          <p className="small muted" style={{ marginTop: 8 }}>
            Write at least one line — even one is plenty.
          </p>
        )}
      </div>
    </section>
  );
}

function EventReview({ onSaved }: { onSaved: (message: string) => void }): React.ReactElement {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [wentWell, setWentWell] = useState('');
  const [wouldChange, setWouldChange] = useState('');
  const [thanks, setThanks] = useState<string[]>(['']);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    if (!title.trim()) return setError('Which event was it? Give it a name.');
    setBusy(true);
    setError(null);
    try {
      const res = await api2.submitReview({
        title,
        wentWell,
        wouldChange,
        thanks: thanks.filter((n) => n.trim()).map((name) => ({ name })),
      });
      onSaved(res.message);
      setOpen(false);
      setTitle('');
      setWentWell('');
      setWouldChange('');
      setThanks(['']);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not send that.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card stack">
      <div>
        <h2>Ran something?</h2>
        <p className="small muted">A short review after a booth, a talk, a poster run — whatever you helped with.</p>
      </div>

      {!open ? (
        <div>
          <button className="btn quiet" onClick={() => setOpen(true)}>
            How did it go?
          </button>
        </div>
      ) : (
        <>
          <div className="field">
            <label htmlFor="ev-title">What was it?</label>
            <input
              id="ev-title"
              type="text"
              value={title}
              placeholder="Saturday farmers market booth"
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="ev-well">What went well?</label>
            <AutoTextarea id="ev-well" value={wentWell} onChange={(e) => setWentWell(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="ev-change">What would you change?</label>
            <AutoTextarea id="ev-change" value={wouldChange} onChange={(e) => setWouldChange(e.target.value)} />
          </div>

          <div className="field">
            <label>Who deserves a thank you?</label>
            <span className="hint">Thank-yous land on their home screens, signed by your coordinator.</span>
            {thanks.map((name, index) => (
              <input
                key={index}
                type="text"
                value={name}
                placeholder="A name"
                style={{ marginTop: 6 }}
                onChange={(e) =>
                  setThanks((prev) => prev.map((existing, i) => (i === index ? e.target.value : existing)))
                }
              />
            ))}
            <button className="btn ghost" style={{ marginTop: 6 }} onClick={() => setThanks((prev) => [...prev, ''])}>
              Add another
            </button>
          </div>

          {error && (
            <p className="notice alert" role="alert">
              {error}
            </p>
          )}
          <div className="row">
            <button className="btn" onClick={() => void save()} disabled={busy}>
              {busy ? 'Sending…' : 'Send'}
            </button>
            <button className="btn ghost" onClick={() => setOpen(false)}>
              Back
            </button>
          </div>
        </>
      )}
    </section>
  );
}

function Questions({
  questions,
  onAsked,
}: {
  questions: QuestionDto[];
  onAsked: (questions: QuestionDto[], message: string) => void;
}): React.ReactElement {
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const ask = async () => {
    if (!body.trim()) return setError('Type your question first.');
    setBusy(true);
    setError(null);
    try {
      const res = await api2.ask(body);
      onAsked(res.questions, res.message);
      setBody('');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not send that.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card stack">
      <div>
        <h2>Ask something</h2>
        <p className="small muted">Answered within a day.</p>
      </div>

      <div className="field">
        <label htmlFor="q">Your question</label>
        <AutoTextarea id="q" value={body} onChange={(e) => setBody(e.target.value)} />
      </div>
      {error && (
        <p className="notice alert" role="alert">
          {error}
        </p>
      )}
      <div>
        <button className="btn" onClick={() => void ask()} disabled={busy}>
          {busy ? 'Asking…' : 'Ask'}
        </button>
      </div>

      {questions.length > 0 && (
        <div className="stack" style={{ gap: 10 }}>
          <span className="kicker">Asked before</span>
          {questions.map((question) => (
            <div key={question.id} className="stack" style={{ gap: 4 }}>
              <p className="small">
                <b>{question.body}</b>
              </p>
              {question.answer ? (
                <p className="small soft">
                  {question.answer}
                  {question.answered_by ? ` — ${question.answered_by}` : ''}
                </p>
              ) : (
                <span className="pill marigold">Waiting for an answer</span>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
